import { useCallback, useMemo } from 'react';

import { URLS, createJurisdictionHeaders } from '@/data';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import { Bet, CashoutBetError, GetBetCashoutAmountResponse } from './types';
import { betKeys } from './use-bets';

const getCashoutAmounts = async (betIds: string[]): Promise<GetBetCashoutAmountResponse> => {
    const resp = await fetch(`${URLS.CHAMELON_API_URL}/auth/cash-out-amount`, {
        method: 'POST',
        headers: {
            ...createJurisdictionHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ bet_group_ids: betIds }),
    });

    const data = await resp.json();

    if (!resp.ok) {
        throw data;
    }
    return data;
};

/** Max acceptable price slippage when selling a predictions position, in basis points (10%). */
const POLYMARKET_CASH_OUT_SLIPPAGE_BPS = 1000;

const cashoutPolymarketBet = async (betId: string): Promise<Bet> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/polymarket/bets/${betId}/cash-out`, {
        method: 'POST',
        headers: {
            ...createJurisdictionHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ slippage_bps: POLYMARKET_CASH_OUT_SLIPPAGE_BPS }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new CashoutBetError(data?.message, data?.error_codes?.[0], data?.errors?.extra?.cashable_amount);
    }

    return data.data;
};

const cashoutBet = async (betId: string, amount: number): Promise<Bet> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/cash-out-bet`, {
        method: 'POST',
        headers: {
            ...createJurisdictionHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ bet_group_id: betId, cashout_amount: amount }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new CashoutBetError(data?.message, data?.error_codes?.[0], data?.errors?.extra?.cashable_amount);
    }

    return data.data;
};

export function useCashoutAmountQuery(betId: string, enabled: boolean) {
    return useQuery({
        queryKey: betKeys.cashoutAmount(betId),
        queryFn: () => getCashoutAmounts([betId]),
        enabled,
        select: data => data.data,
        staleTime: 0,
    });
}

/**
 * Batched cash-out offers — one POST for every visible eligible entry, keyed off the sorted id set (the
 * bet-history list uses this instead of one query per card). Returns `{ [betId]: number | null }`.
 */
export function useCashoutAmountsQuery(betIds: string[]) {
    const sortedIds = useMemo(() => [...betIds].sort(), [betIds]);
    const query = useQuery({
        queryKey: [...betKeys.cashoutAmounts(), { ids: sortedIds }],
        queryFn: async () => await getCashoutAmounts(sortedIds),
        enabled: sortedIds.length > 0,
        select: data => data.data,
        staleTime: 0,
        // The id set rekeys on every cash-out / page load; keep the last offers so the other CTAs don't blank out.
        placeholderData: keepPreviousData,
    });
    // react-query's manual refetch ignores `enabled`, so guard it too — a focus / resume / pull-to-refresh
    // with no eligible ids must not POST an empty id set.
    const { refetch } = query;
    const guardedRefetch = useCallback(async () => {
        if (sortedIds.length > 0) {
            await refetch();
        }
    }, [sortedIds.length, refetch]);
    return { ...query, refetch: guardedRefetch };
}

export const useCashoutBet = () => {
    const isPredictions = useIsPredictionsProduct();
    return useMutation({
        mutationKey: ['cashout-bet'],
        mutationFn: ({ betId, amount }: { betId: string; amount: number }) =>
            isPredictions ? cashoutPolymarketBet(betId) : cashoutBet(betId, amount),
    });
};
